import { useState, useEffect } from "react";
import { ViewType } from "./types/navigation";
import { LoginForm } from "./components/auth/LoginForm";
import { ForgotPasswordForm } from "./components/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "./components/auth/ResetPasswordForm";
import { ChangePasswordModal } from "./components/auth/ChangePasswordModal";
import { AppLayout } from "./components/layout/AppLayout";
import { AppRouter } from "./components/layout/AppRouter";
import { useTimeBasedTheme } from "./hooks/useTimeBasedTheme";
import { useAuthSession } from "./hooks/useAuthSession";
import { useAppData } from "./hooks/useAppData";
import { effectiveRoles, userHasRole } from "./utils/accessControl";
import { navigateToView, viewFromLocation } from "./utils/appRoutes";

export default function App() {
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    if (typeof window === "undefined") return "dashboard";
    return viewFromLocation(window.location) ?? "dashboard";
  });

  const {
    currentUser,
    authView,
    resetToken,
    handleLogin: authLogin,
    handleLogout,
    handleUpdateCurrentUser: authUpdateCurrentUser,
    handleShowForgotPassword,
    handleBackToMain,
  } = useAuthSession(() => {
    loadDataFromDatabase();
  });

  const {
    tasks,
    users,
    projects,
    workLogs,
    materials,
    setUsers,
    loadDataFromDatabase,
    handleCreateProject,
    handleUpdateProject,
    handleDeleteProject,
    handleArchiveProject,
    handleRestoreProject,
    handleAssignFabricator,
    handleBroadcastFabricators,
    handleAcceptAssignment,
    handleDeclineAssignment,
    handleUpdateTaskStatus,
    handleCreateTask,
    handleUpdateTask,
    handleDeleteTask,
    handleAddWorkLog,
    handleUpdateWorkLog,
    handleDeleteWorkLog,
    handleAddMaterial,
    handleUpdateMaterial,
    handleDeleteMaterial,
    handleAddUser,
  } = useAppData(currentUser);

  // Time-based theme configuration
  const themeStorageKey = currentUser
    ? `theme-override-${currentUser.role}`
    : "theme-override-guest";
  const { isTransitioning, setTheme, getCurrentTheme } = useTimeBasedTheme({
    storageKey: themeStorageKey,
  });

  // Login handler
  const handleLogin = (user: typeof currentUser extends null ? never : any) => {
    authLogin(user);
    setCurrentView("dashboard");
    navigateToView("dashboard", true);
  };

  const [isChangePasswordDismissed, setIsChangePasswordDismissed] = useState(false);
  useEffect(() => {
    setIsChangePasswordDismissed(false);
  }, [currentUser?.id]);
  const shouldPromptChangePassword = Boolean(
    currentUser?.mustChangePassword && !isChangePasswordDismissed
  );

  const handleUpdateCurrentUser = (
    updatedUser: typeof currentUser extends null ? never : any
  ) => {
    authUpdateCurrentUser(updatedUser);
    handleAddUser(updatedUser);
  };

  // Role-based view guards
  useEffect(() => {
    if (!currentUser) return;

    const allowedByRole: Record<string, ReadonlyArray<ViewType>> = {
      sysadmin: [
        "dashboard",
        "projects",
        "tasks",
        "users",
        "materials",
        "reports",
        "settings",
        "team",
        "revenue",
        "assignments",
        "archives",
        "activity-logs",
        "billing",
        "feature-bank",
      ],
      executive: [
        "dashboard", "projects", "tasks", "users", "materials", "reports",
        "settings", "team", "revenue", "assignments", "archives",
        "activity-logs", "billing", "feature-bank",
      ],
      director: [
        "dashboard", "projects", "tasks", "team", "reports", "archives",
        "revenue", "activity-logs", "feature-bank", "settings",
      ],
      leader: [
        "dashboard",
        "projects",
        "tasks",
        "team",
        "reports",
        "feature-bank",
        "settings",
      ],
      fabricator: [
        "dashboard",
        "projects",
        "assignments",
        "worklog",
        "materials",
        "tasks",
        "settings",
        "feature-bank",
      ],
      client: ["dashboard", "project-status", "documentation", "billing"],
    };

    const allowed = [...new Set(
      effectiveRoles(currentUser).flatMap((role) => allowedByRole[role] ?? []),
    )];
    if (allowed.length === 0) allowed.push("dashboard");
    if (!allowed.includes(currentView)) {
      setCurrentView("dashboard");
      navigateToView("dashboard", true);
    }
  }, [currentUser, currentView]);

  // Keep application state synchronized with clean browser-history routes.
  useEffect(() => {
    const applyLocation = () => {
      const view = viewFromLocation(window.location);
      if (!view) {
        if (currentUser) navigateToView("dashboard", true);
        return;
      }
      if (view === "assignments" && userHasRole(currentUser, "fabricator")) {
        navigateToView("projects", true);
        return;
      }
      setCurrentView(view);
      if (window.location.hash) navigateToView(view, true);
    };

    applyLocation();
    window.addEventListener("popstate", applyLocation);

    return () => {
      window.removeEventListener("popstate", applyLocation);
    };
  }, [currentUser]);

  if (!currentUser) {
    return (
      <>
        {authView === "forgot-password" && (
          <ForgotPasswordForm onBackToLogin={handleBackToMain} />
        )}
        {authView === "reset-password" && (
          <ResetPasswordForm
            token={resetToken}
            onBackToLogin={handleBackToMain}
          />
        )}
        {authView === "main" && (
          <LoginForm
            onLogin={handleLogin}
            onShowForgotPassword={handleShowForgotPassword}
          />
        )}
      </>
    );
  }

  return (
    <><AppLayout
      currentUser={currentUser}
      onLogout={handleLogout}
      currentTheme={getCurrentTheme()}
      onThemeChange={setTheme}
      isTransitioning={isTransitioning}
    >
      <AppRouter
        currentUser={currentUser}
        currentView={currentView}
        projects={projects}
        tasks={tasks}
        users={users}
        workLogs={workLogs}
        materials={materials}
        setUsers={setUsers}
        onCreateProject={
          currentUser.role === "admin" || currentUser.role === "supervisor"
            ? handleCreateProject
            : undefined
        }
        onUpdateProject={handleUpdateProject}
        onDeleteProject={handleDeleteProject}
        onArchiveProject={handleArchiveProject}
        onRestoreProject={handleRestoreProject}
        onAssignFabricator={
          currentUser.role === "supervisor"
            ? handleAssignFabricator
            : undefined
        }
        onBroadcastFabricators={
          currentUser.role === "supervisor"
            ? handleBroadcastFabricators
            : undefined
        }
        onAcceptAssignment={handleAcceptAssignment}
        onDeclineAssignment={handleDeclineAssignment}
        onCreateTask={handleCreateTask}
        onUpdateTask={handleUpdateTask}
        onUpdateTaskStatus={handleUpdateTaskStatus}
        onDeleteTask={handleDeleteTask}
        onAddWorkLog={handleAddWorkLog}
        onUpdateWorkLog={handleUpdateWorkLog}
        onDeleteWorkLog={handleDeleteWorkLog}
        onAddMaterial={handleAddMaterial}
        onUpdateMaterial={handleUpdateMaterial}
        onDeleteMaterial={handleDeleteMaterial}
        onAddUser={handleAddUser}
        onUpdateCurrentUser={handleUpdateCurrentUser}
      />
    </AppLayout>
    <ChangePasswordModal
      isOpen={shouldPromptChangePassword}
      currentUser={currentUser}
      onClose={() => setIsChangePasswordDismissed(true)}
      onSuccess={(updatedUser) => {
        handleUpdateCurrentUser(updatedUser);
        setIsChangePasswordDismissed(true);
      }}
    />
    </>
  );
}
